// Choose Workout presentation helpers (Phase 7 checkpoint 7E). Classification
// comes only from the server's `projectionIfStartedNow` (§15.6); nothing here
// reads dates, week numbers or completion to decide whether a workout counts.

const WEEKDAYS = Object.freeze([
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
]);
const SHORT_MONTHS = Object.freeze([
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]);

// The only two canonical row labels. `weekNumber` is the row's own target
// week, which is what a PLANNED projection counts toward.
export function projectionLabel(projection, weekNumber = null) {
  if (!projection?.outcome) return null;
  if (projection.outcome === "PLANNED" && weekNumber != null) {
    return { tone: "counts", text: `Counts · Week ${weekNumber}` };
  }
  return { tone: "extra", text: "Extra if done now" };
}

// The identity fields StartWorkoutScreen's `sourceFromSearch` reads for each
// source kind. A row provides identity only; Start builds everything else.
const START_TARGET_FIELDS = Object.freeze({
  CYCLE_OCCURRENCE: ["cycleId", "occurrenceKey"],
  CYCLE_WORKOUT: ["cycleId", "workoutId"],
  WEEKLY_PLAN_WORKOUT: ["weeklyPlanParentId", "workoutId"],
});

export function startPathForTarget(target) {
  const fields = START_TARGET_FIELDS[target?.kind];
  if (!fields || fields.some((field) => !target[field])) return null;
  const params = new URLSearchParams({ kind: target.kind });
  fields.forEach((field) => params.set(field, target[field]));
  return `/train/start?${params}`;
}

function parseDateKey(dateKey) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey || ""));
  return match ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) } : null;
}

export function weekdayName(dateKey) {
  const parts = parseDateKey(dateKey);
  if (!parts) return "";
  return WEEKDAYS[new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()];
}

export function formatShortDate(dateKey) {
  const parts = parseDateKey(dateKey);
  return parts ? `${SHORT_MONTHS[parts.month - 1]} ${parts.day}` : "";
}

export function cycleDateRange(startDate, endDate) {
  const start = formatShortDate(startDate);
  const end = formatShortDate(endDate);
  const startParts = parseDateKey(startDate);
  const endParts = parseDateKey(endDate);
  if (!start || !end) return "";
  const weeks = Math.round(
    (Date.UTC(endParts.year, endParts.month - 1, endParts.day) -
      Date.UTC(startParts.year, startParts.month - 1, startParts.day)) / 86400000 / 7
  );
  const range = startParts.year === endParts.year
    ? `${start} – ${end}, ${endParts.year}`
    : `${start}, ${startParts.year} – ${end}, ${endParts.year}`;
  return weeks > 0 ? `${range} · ${weeks} ${weeks === 1 ? "week" : "weeks"}` : range;
}

export function countLabel(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

// The server's estimated workout duration (approved Phase 7 amendment), shown
// as-is. Presentation only: nothing here estimates, derives or rounds a
// duration, and a missing or unusable value is simply omitted.
export function durationLabel(estimatedDurationMinutes) {
  return Number.isSafeInteger(estimatedDurationMinutes) && estimatedDurationMinutes > 0
    ? `~${estimatedDurationMinutes} min`
    : null;
}

// "6 exercises · ~55 min" parts for a source row, from server values only.
export function workoutMetricParts({ exerciseCount, estimatedDurationMinutes } = {}) {
  return [
    Number.isSafeInteger(exerciseCount) && exerciseCount >= 0
      ? countLabel(exerciseCount, "exercise")
      : null,
    durationLabel(estimatedDurationMinutes),
  ].filter(Boolean);
}

// Week header summary from the server's `completed` flags (display only).
export function weekSummary(occurrences = []) {
  const completed = occurrences.filter((occurrence) => occurrence.completed).length;
  return completed
    ? `${completed} of ${occurrences.length} completed`
    : countLabel(occurrences.length, "workout");
}

// Current week expanded, the others collapsed (Phase 7 §5). An upcoming cycle
// has no current week, so its first week opens.
export function defaultExpandedWeek(currentCycle) {
  const weeks = currentCycle?.weeks || [];
  const current = currentCycle?.cycle?.currentWeekNumber;
  if (weeks.some((week) => week.weekNumber === current)) return current;
  return weeks[0]?.weekNumber ?? null;
}
